import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { allowLeave } from '@ipfabric/ui-components/LeaveDialog/LeaveDialog';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import type { MakeHandleSubmitFn } from '@ipfabric/ui-components/utils/formSnippets/useFormComposition';
import { WizardStepper } from 'app/features/DiscoverySettingsForms/composition/DiscoveryConfigWizard/WizardStepper';
import {
  type StepIndex,
  isStepIndex,
} from 'app/features/DiscoverySettingsForms/composition/DiscoveryConfigWizard/configWizard.types';
import { wizardStepsConfig } from 'app/features/DiscoverySettingsForms/composition/DiscoveryConfigWizard/wizardConfig';
import {
  disableStartBtn,
  enableStartBtn,
} from 'pages/SnapshotManagement/actions';
import { PAGE_URL as SNAPSHOT_SETTINGS_PAGE } from 'pages/SnapshotManagement/constants';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useApi } from 'utils/request/useApi';
import type { DiscoverySettingsObject } from '../model/DiscoverySettingsObject';
import { handleDiscoveryError } from '../utils/handleDiscoveryError';

const FIRST_STEP_INDEX = 0;

const getStepIndexFromUrl = (path: string): StepIndex | null => {
  const stepFromUrl = wizardStepsConfig.find((step) => step.path === path);
  if (stepFromUrl) {
    const stepIndex = wizardStepsConfig.indexOf(stepFromUrl);
    if (isStepIndex(stepIndex)) {
      return stepIndex;
    }
  }
  return null;
};

export const DiscoveryConfigWizardPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const api = useApi();
  const notifications = useNotifications();
  const reduxDispatch = useDispatch();

  const stepFromUrl = getStepIndexFromUrl(location.pathname);
  const currentStep = isStepIndex(stepFromUrl) ? stepFromUrl : FIRST_STEP_INDEX;

  useEffect(() => {
    const stepFromUrl = getStepIndexFromUrl(location.pathname);

    // If URL contains invalid step, redirect to first step
    if (stepFromUrl === null) {
      navigate(wizardStepsConfig[0].path, { replace: true });
      return;
    }
  }, [location.pathname]);

  const navigateToStep = useStableCallback((step: StepIndex) => {
    const newPath = wizardStepsConfig[step]?.path;
    if (newPath) {
      navigate(newPath);
    } else {
      stumble('DiscoveryConfigWizardPage: step path not found');
    }
  });

  const goToPreviousStep = useStableCallback(() => {
    const previousStep = currentStep - 1;
    if (isStepIndex(previousStep)) {
      navigateToStep(previousStep);
    } else {
      stumble('DiscoveryConfigWizardPage: previous step index is not valid');
    }
  });

  const goToNextStep = useStableCallback(() => {
    const nextStep = currentStep + 1;
    if (isStepIndex(nextStep)) {
      navigateToStep(nextStep);
    } else {
      stumble('DiscoveryConfigWizardPage: next step index is not valid');
    }
  });

  const handleStepSelect = useStableCallback(
    async (step: StepIndex, makeHandleSubmit?: MakeHandleSubmitFn) => {
      if (makeHandleSubmit) {
        makeHandleSubmit?.(() => allowLeave(() => navigateToStep(step)))();
      } else {
        // No need to confirm navigation if there's no form to submit
        navigateToStep(step);
      }
    },
  );

  const handleDiscoverySubmit = useStableCallback(
    async (discoverySettingsObject: DiscoverySettingsObject) => {
      const response = await api.request({
        url: '/settings',
        method: 'PATCH',
        data: discoverySettingsObject,
      });
      notifications.showNotification({
        type: 'success',
        message: 'Settings saved',
      });
      return response;
    },
  );

  const handleFinalSubmit = useStableCallback(
    async (discoverySettingsObject: DiscoverySettingsObject) => {
      try {
        // In case discovery starts too late or other discovery is running when user is redirected to snapshot settings,
        // we don't want user to have start button enabled. Start button is controlled by socket events to be enabled
        // when discovery finishes or fails.
        reduxDispatch(disableStartBtn());
        await api.request({
          url: '/snapshots',
          method: 'POST',
          data: discoverySettingsObject,
        });
        await new Promise((resolve) =>
          setTimeout(() => {
            resolve(true);
            navigate(SNAPSHOT_SETTINGS_PAGE);
            // Experimental timeout 5600 to wait for animation and discovery to start. It could happen that user would be
            // redirected to snapshot settings page too early, before snapshot is acually running. It isn't guaranteed
            // that 5600ms is enough in all cases, but it's better than 4500 animation duration.
          }, 5600),
        );
      } catch (e: unknown) {
        // This one is currently controlled by socket events not to enable start button too early.
        // So it's "correct" to have it in `catch` branch instead of `finally`.
        reduxDispatch(enableStartBtn());
        handleDiscoveryError({
          error: e,
          showNotification: notifications.showNotification,
        });
      }
    },
  );

  const wizardStepper = useStableCallback(
    (
      isSubmitting?: boolean,
      makeHandleSubmit?: MakeHandleSubmitFn,
      disabled?: boolean,
    ) => (
      <WizardStepper
        selectedStep={currentStep}
        isSubmitting={isSubmitting}
        onSelect={(step) => handleStepSelect(step, makeHandleSubmit)}
        disabled={disabled}
      />
    ),
  );

  const StepComponent = wizardStepsConfig[currentStep].Component;

  return (
    <StepComponent
      key={currentStep}
      onSubmit={handleDiscoverySubmit}
      wizardStepper={wizardStepper}
      onPreviousStep={goToPreviousStep}
      onNextStep={goToNextStep}
      onFinalSubmit={handleFinalSubmit}
    />
  );
};
